'use client';

import Link from 'next/link';
import { useRouter, useSearchParams } from 'next/navigation';
import { Suspense, useEffect, useState } from 'react';
import { generateIdentity, loadIdentity, saveIdentity, type Identity } from '../../lib/identity';
import { createAccount, ApiCallFailed } from '../../lib/api';

type Step =
  | { kind: 'checking' }
  | { kind: 'idle' }
  | { kind: 'showing'; identity: Identity; backed_up: boolean }
  | { kind: 'submitting' }
  | { kind: 'error'; message: string };

// Whitelist of safe redirect destinations after signup. Anything
// matching one of these prefixes is allowed; everything else falls
// through to /dashboard. Prevents an attacker-crafted URL like
// /signup?next=https://evil.example from harvesting freshly-minted
// identities mid-flow.
const NEXT_PREFIXES = ['/dashboard', '/subscribe', '/methods'];

function safeNext(raw: string | null): string {
  if (!raw) return '/dashboard';
  // Must be relative (start with `/`), not protocol-prefixed.
  if (!raw.startsWith('/') || raw.startsWith('//')) return '/dashboard';
  // Must match a whitelisted prefix (path-only check; query+fragment ok).
  const path_only = raw.split('?', 1)[0]!.split('#', 1)[0]!;
  if (!NEXT_PREFIXES.some((p) => path_only === p || path_only.startsWith(p + '/'))) return '/dashboard';
  return raw;
}

export default function SignupPage() {
  // Suspense boundary required by Next 15 when a child uses
  // useSearchParams — without it the static prerender bails with
  // "Error occurred prerendering page /signup".
  return (
    <Suspense fallback={
      <main className="container-x py-24 text-sm text-ink-dim text-center">Loading…</main>
    }>
      <SignupPageInner />
    </Suspense>
  );
}

function SignupPageInner() {
  const router = useRouter();
  const search = useSearchParams();
  const next   = safeNext(search?.get('next') ?? null);
  const [step, setStep] = useState<Step>({ kind: 'checking' });

  // Pressing "Generate keypair" calls saveIdentity which REPLACES
  // (not merges) the IndexedDB record — silently overwriting an
  // active identity would lose access to whatever account the
  // browser already had. If we find an existing identity here,
  // route to /dashboard instead of letting the user near the
  // generator.
  useEffect(() => {
    let cancelled = false;
    loadIdentity()
      .then((id) => {
        if (cancelled) return;
        if (id) router.replace(next);
        else    setStep({ kind: 'idle' });
      })
      .catch(() => { if (!cancelled) setStep({ kind: 'idle' }); });
    return () => { cancelled = true; };
  }, [router, next]);

  function onGenerate() {
    try {
      const identity = generateIdentity();
      setStep({ kind: 'showing', identity, backed_up: false });
    } catch (err) {
      setStep({ kind: 'error', message: (err as Error).message });
    }
  }

  async function onContinue() {
    if (step.kind !== 'showing') return;
    setStep({ kind: 'submitting' });
    try {
      await saveIdentity(step.identity);
      await createAccount(step.identity.account_id);
      router.push(next);
    } catch (err) {
      const msg = err instanceof ApiCallFailed
        ? `${err.status}: ${err.body && 'error' in err.body ? err.body.error : 'request failed'}`
        : (err as Error).message;
      setStep({ kind: 'error', message: msg });
    }
  }

  return (
    <main className="min-h-[calc(100vh-3.5rem)] flex items-center justify-center container-x py-16">
      <div className="w-full max-w-md card">
        <h1 className="heading-2">Get started.</h1>
        <p className="mt-2 text-sm text-ink-dim">
          Your browser is going to mint a fresh secp256k1 keypair. The pubkey becomes your
          account; the privkey stays on this device, exported as <span className="font-mono">WIF</span>.
          Back it up to paper before you make any payment — nobody on our side can recover it.
        </p>

        {step.kind === 'checking' && (
          <p className="mt-8 text-sm text-ink-dim text-center">Checking for an existing identity…</p>
        )}

        {step.kind === 'idle' && (
          <button onClick={onGenerate} className="btn-primary w-full mt-8">
            Generate keypair
          </button>
        )}

        {step.kind === 'showing' && (
          <KeypairReview
            identity={step.identity}
            backedUp={step.backed_up}
            onToggleBackedUp={() => setStep({ ...step, backed_up: !step.backed_up })}
            onContinue={onContinue}
          />
        )}

        {step.kind === 'submitting' && (
          <p className="mt-8 text-sm text-ink-dim text-center">Creating account…</p>
        )}

        {step.kind === 'error' && (
          <div className="mt-6 rounded-md bg-red-500/10 ring-1 ring-inset ring-red-500/30 p-4">
            <div className="text-sm font-medium text-red-300">Something went wrong</div>
            <p className="mt-1 text-xs text-red-200/80 break-words">{step.message}</p>
            <button onClick={() => setStep({ kind: 'idle' })}
                    className="btn-secondary text-sm mt-4">Try again</button>
          </div>
        )}

        <div className="mt-8 pt-6 border-t border-white/5 text-center text-sm">
          <span className="text-ink-dim">Already have an account?</span>{' '}
          <Link href="/login" className="text-accent hover:text-accent-muted">Log in</Link>
        </div>
      </div>
    </main>
  );
}

function KeypairReview(props: {
  identity:        Identity;
  backedUp:        boolean;
  onToggleBackedUp: () => void;
  onContinue:      () => void;
}) {
  return (
    <div className="mt-8 space-y-4">
      <div>
        <div className="text-xs uppercase tracking-wider text-ink-faint">Account ID (public)</div>
        <code className="block mt-1 font-mono text-xs text-accent break-all">
          {props.identity.account_id}
        </code>
      </div>
      <div>
        <div className="text-xs uppercase tracking-wider text-ink-faint flex items-center gap-2">
          Recovery key (WIF — private)
          <CopyButton value={props.identity.wif} />
        </div>
        <code className="block mt-1 font-mono text-xs text-ink break-all bg-surface-2 ring-1 ring-inset ring-white/5 rounded p-3">
          {props.identity.wif}
        </code>
        <p className="mt-2 text-xs text-ink-faint">
          Write this down or save to a password manager. We can't recover it for you. Anyone
          with this string controls your account and balance.
        </p>
      </div>
      <label className="flex items-center gap-3 text-sm text-ink-dim cursor-pointer select-none">
        <input
          type="checkbox"
          checked={props.backedUp}
          onChange={props.onToggleBackedUp}
          className="h-4 w-4 accent-accent"
        />
        I've saved my recovery key.
      </label>
      <button
        onClick={props.onContinue}
        disabled={!props.backedUp}
        className="btn-primary w-full disabled:opacity-50 disabled:cursor-not-allowed"
      >
        Continue
      </button>
    </div>
  );
}

function CopyButton({ value }: { value: string }) {
  const [copied, setCopied] = useState(false);
  return (
    <button
      type="button"
      onClick={async () => {
        try { await navigator.clipboard.writeText(value); setCopied(true); setTimeout(() => setCopied(false), 1200); }
        catch { /* clipboard blocked — operator copies by selection */ }
      }}
      className="ml-auto text-[10px] uppercase tracking-wider text-ink-faint hover:text-ink"
    >
      {copied ? 'copied' : 'copy'}
    </button>
  );
}
